import type { ConditionGroup, CreateProspectPayload, NextPageParams, PaginationMetadata, Prospect } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"
import type { CreateOpportunityParams } from "@/hooks/api/opportunities/api"
import { BulkAddHouseholdsToProjectResponse } from "@/hooks/api/projects/types"

export interface ProspectsResponse {
  prospects: Prospect[]
  pagination: PaginationMetadata
}

export function fetchProspects(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  listId?: string
) {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  if (listId) {
    params.append("list_id", listId)
  }

  return apiContextFetch<ProspectsResponse>(context, `prospects?${params.toString()}`)
}

export function fetchProspect(context: ApiContext, id: string) {
  return apiContextFetch<Prospect>(context, `prospects/${id}`)
}

export function createProspect(
  context: ApiContext,
  params: CreateProspectPayload,
  overrideBookId?: string,
  opportunity?: CreateOpportunityParams & { pipeline_id: string }
) {
  return apiContextFetch<Prospect>(context, "prospects", {
    method: "POST",
    body: JSON.stringify({ prospect: params, opportunity, override_book_id: overrideBookId }),
  })
}

export interface UpdateProspectPayload {
  custom_fields_attributes?: Record<string, unknown>
  [key: string]: unknown
}

export function updateProspect(context: ApiContext, id: string, params: UpdateProspectPayload) {
  return apiContextFetch<Prospect>(context, `prospects/${id}`, {
    method: "PUT",
    body: JSON.stringify({ prospect: params }),
  })
}

export interface AllProspectIdsResponse {
  prospect_ids: string[]
  total_count: number
}

export function fetchAllProspectIds(context: ApiContext, viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  return apiContextFetch<AllProspectIdsResponse>(context, `prospects/all_ids?${params.toString()}`, {
    method: "GET",
  })
}

export interface BulkUpdateProspectsPayload {
  prospect_ids: string[]
  tag_id?: string
  remove_tag_id?: string
  custom_field_key?: string
  custom_field_value?: string | string[] | number | boolean | null
}

export function bulkUpdateProspects(context: ApiContext, params: BulkUpdateProspectsPayload) {
  return apiContextFetch<{ updated_count: number }>(context, "prospects/bulk_update", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export interface BulkPromoteProspectsPayload {
  prospect_ids: string[]
}

export function bulkPromoteToClient(context: ApiContext, params: BulkPromoteProspectsPayload) {
  return apiContextFetch<{ promoted_count: number; failed_count: number }>(
    context,
    "prospects/bulk_promote_to_client",
    {
      method: "POST",
      body: JSON.stringify(params),
    }
  )
}

export interface BulkAddProspectsToSequencePayload {
  prospect_ids: string[]
  sequence_id: string
  email_integration_id?: string | null
  sms_integration?: string | null
}

export interface BulkAddProspectsToSequenceResponse {
  queued_count: number
  message: string
}

export function bulkAddProspectsToSequence(context: ApiContext, params: BulkAddProspectsToSequencePayload) {
  return apiContextFetch<BulkAddProspectsToSequenceResponse>(context, "prospects/bulk_add_to_sequence", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface BulkAddProspectsToProjectPayload {
  prospect_ids: string[]
  project_template_id: string
  assigned_to_id: string | null
  assigned_team_id: string | null
}

export function bulkAddProspectsToProject(context: ApiContext, params: BulkAddProspectsToProjectPayload) {
  return apiContextFetch<BulkAddHouseholdsToProjectResponse>(context, "prospects/bulk_add_to_project", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface BulkCreateProspectsPayload {
  file_name?: string
  override_duplicates?: boolean
  records: Array<{
    household_id?: string
    contact_type?: string
    full_name?: string
    first_name?: string
    last_name?: string
    email?: string
    phone_number?: string
    tier?: string
    address_line1?: string
    address_line2?: string
    address_city?: string
    address_state?: string
    address_zip?: string
    address_country_code?: string
    address_type?: string
    is_primary_address?: boolean
    address_2_line1?: string
    address_2_line2?: string
    address_2_city?: string
    address_2_state?: string
    address_2_zip?: string
    address_2_country_code?: string
    address_2_type?: string
    // Protected/additional person fields
    ssn?: string
    date_of_birth?: string
    gender?: string
    marital_status?: string
    job_title?: string
    salutation?: string
    suffix?: string
    middle_name?: string
    preferred_name?: string
    previous_name?: string
    designations?: string
    // Custom fields
    custom_fields?: Record<string, unknown>
    person_custom_fields?: Record<string, unknown>
  }>
}

export interface BulkCreateResponse {
  list_id: string
}

export function bulkCreateProspects(context: ApiContext, params: BulkCreateProspectsPayload) {
  return apiContextFetch<BulkCreateResponse>(context, "prospects/bulk_create", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export function bulkDestroyProspects(context: ApiContext, prospectIds: string[]) {
  return apiContextFetch<Prospect>(context, `prospects/bulk_destroy`, {
    method: "DELETE",
    body: JSON.stringify({ prospect_ids: prospectIds }),
  })
}

export interface BulkRenameProspectsPayload {
  prospect_ids: string[]
  templates: {
    include_spouse: boolean
    no_spouse_template: string
    same_last_name_template: string
    different_last_name_template: string
  }
}

export function bulkRenameProspects(context: ApiContext, params: BulkRenameProspectsPayload) {
  return apiContextFetch<{ renamed_ids: string[] }>(context, "prospects/bulk_rename", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}
